'use client';
import React, { useState, useCallback, useMemo } from 'react';

import { Typography } from '@mui/material';
import styled from 'styled-components';

import EditInAdminButton from '../EditInAdminButton';
import Button from '@/ui/components/Button';
import { IconName } from '@/ui/components/Button/Icons';
import Icon from '@/ui/components/Icon';
import { replaceLocalUrl, descriptionCute } from '@/utils/helpers';

const ThumbnailOverlay = styled.div`
  align-items: center;
  color: red;
  cursor: pointer;
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;

  &:before {
    background: #fff;
    content: '';
    display: block;
    height: 20px;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 20px;
    z-index: 1;
  }

  svg {
    height: auto;
    position: relative;
    transition: transform 250ms ease-out;
    width: 25%;
    z-index: 2;
  }
`;

const ArticleItemWrap = styled.div<{ compact?: boolean; noBorder?: boolean }>`
  position: relative;

  & + &,
  &:first-of-type {
    border-top: ${({ noBorder, theme }) => (noBorder ? 'none' : `1px solid ${theme.custom.colors.gray200}`)};
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin-top: 30px;
    padding-top: 30px;
  }

  a {
    align-items: center;
    display: ${({ compact }) => (compact ? 'flex' : 'block')};
    color: ${props => props.theme.palette.text.primary};
    cursor: pointer;
    overflow: hidden;
    padding: 30px 264px 30px 20px;
    ${({ compact = false }) => compact && 'padding-right: 20px;'}
    position: relative;
    text-decoration: none;
    width: 100%;

    ${({ theme }) => theme.breakpoints.down('md')} {
      padding: 30px 244px 30px 0px;
    }

    ${({ theme }) => theme.breakpoints.down('sm')} {
      overflow: unset;
      padding: 0;
    }

    &:hover {
      span {
        text-decoration: none;
      }

      ${ThumbnailOverlay} svg {
        transform: scale(1.1);
      }
    }
  }

  h3 {
    ${({ compact = false }) => compact && 'font-weight: 400;'}
    margin-top: 0;

    ${({ theme }) => theme.breakpoints.down('sm')} {
      ${({ compact }) => !compact && 'margin-top: 30px;'}
    }
  }

  p {
    margin-bottom: 15px;
    margin-top: 15px;
  }

  span {
    text-decoration: underline;
  }
`;

const Thumbnail = styled.div`
  display: block;
  float: right;
  height: 120px;
  margin-right: -244px;
  position: relative;
  width: 214px;

  img {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    aspect-ratio: 16/9;
    float: none;
    height: auto;
    width: 100%;
  }
`;

const ArticleIconWrap = styled.div`
  color: ${props => props.theme.palette.primary.main};
  margin-right: 15px;
`;

const VideoContainerWrap = styled.div`
  padding: 30px 20px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding: 0;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  max-width: 100%;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

/**
 * @todo adjust for compact prop - two vriants of the component
 */
interface ArticleItemProps {
  href?: string;
  imgSrc?: string;
  imgAlt?: string;
  title: string;
  description?: string;
  buttonLabel?: string;
  buttonIcon?: IconName;
  compact?: boolean;
  noBorder?: boolean;
  type?: 'text' | 'video';
  videoLink?: string;
  item?: RestDataPayload;
}

const getYoutubeId = (url: string): string => {
  if (!url) return '';

  const regExp =
    /^.*(?:(?:youtu\.be\/|v\/|vi\/|u\/\w\/|embed\/|shorts\/)|(?:(?:watch)?\?v(?:i)?=|&v(?:i)?=))([^#&?]*).*/;
  const match = url.match(regExp);

  return match?.[1] ?? '';
};

const ArticleItem: React.FC<ArticleItemProps> = React.memo(
  ({
    href,
    imgSrc,
    imgAlt = '',
    title,
    description = '',
    buttonLabel,
    buttonIcon,
    compact = false,
    noBorder = false,
    type = 'text',
    videoLink,
    item = {},
  }) => {
    const [showVideo, setShowVideo] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const handleVideoClick = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setShowVideo(true);
    }, []);

    const handleMouseEnter = useCallback(() => setIsHovered(true), []);
    const handleMouseLeave = useCallback(() => setIsHovered(false), []);

    const adjustedDescription = useMemo(() => descriptionCute(description), [description]);

    const youtubeId = useMemo(() => getYoutubeId(videoLink || ''), [videoLink]);

    const processedImgSrc = useMemo(() => replaceLocalUrl(imgSrc || ''), [imgSrc]);

    if (compact)
      return (
        <ArticleItemWrap compact noBorder={noBorder} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <EditInAdminButton item={item} visible={isHovered} />

          <a href={href}>
            <ArticleIconWrap>
              <Icon name="file-lines" size="2xl" />
            </ArticleIconWrap>

            <Typography variant="h5" component="h3">
              {title}
            </Typography>
          </a>
        </ArticleItemWrap>
      );

    return (
      <ArticleItemWrap onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <EditInAdminButton item={item} visible={isHovered} />

        {!showVideo ? (
          <a href={videoLink ? undefined : href} {...(videoLink ? { onClick: handleVideoClick } : {})}>
            <Thumbnail>
              <img src={processedImgSrc} alt={imgAlt} />
              {type === 'video' && (
                <ThumbnailOverlay>
                  <Icon name="youtube" style="brands" />
                </ThumbnailOverlay>
              )}
            </Thumbnail>

            <Typography variant="h5" component="h3">
              {title}
            </Typography>

            <p>{adjustedDescription}</p>

            <div>
              <Button tabIndex="-1" label={buttonLabel} variant="text" icon={buttonIcon} iconSize={26} />
            </div>
          </a>
        ) : (
          <VideoContainerWrap>
            {videoLink && (
              <VideoContainer>
                <iframe
                  src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
                  title={title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  frameBorder="0"
                />
              </VideoContainer>
            )}
          </VideoContainerWrap>
        )}
      </ArticleItemWrap>
    );
  }
);

ArticleItem.displayName = 'ArticleItem';

export default ArticleItem;
